import { request } from './client';

/** Auth provider identity types supported by Hub. */
export type AuthProvider = 'google' | 'telegram' | 'wallet';

/** A single active session returned by GET /api/me/sessions. */
export interface SettingsSessionRow {
  session_id: string;
  created_at: string;
  last_used_at: string | null;
  user_agent: string | null;
  ip: string | null;
  is_current: boolean;
}

/** Response from PATCH /api/me. */
export interface UpdateMeResponse {
  userId: string;
  displayName: string | null;
}

/** Response from GET /api/me/sessions. */
export interface SessionsResponse {
  rows: SettingsSessionRow[];
}

/** Response from POST /api/me/sessions/logout-all. */
export interface LogoutAllResponse {
  logged_out_count: number;
}

/** Response from DELETE /api/me/identities/:provider. */
export interface UnlinkResponse {
  remaining_identities: AuthProvider[];
}

/** Response from DELETE /api/me (account deletion). */
export interface DeleteAccountResponse {
  ok: true;
}

export const meApi = {
  /**
   * Update the signed-in user's profile.
   * PATCH /api/me  { display_name: string | null }
   */
  updateProfile(displayName: string | null): Promise<UpdateMeResponse> {
    return request<UpdateMeResponse>('/api/me', {
      method: 'PATCH',
      body: { display_name: displayName },
    });
  },

  /**
   * List all active sessions for the current user.
   * GET /api/me/sessions
   */
  sessions(): Promise<SessionsResponse> {
    return request<SessionsResponse>('/api/me/sessions');
  },

  /**
   * Revoke all sessions except the current one.
   * POST /api/me/sessions/logout-all
   */
  logoutAllSessions(): Promise<LogoutAllResponse> {
    return request<LogoutAllResponse>('/api/me/sessions/logout-all', { method: 'POST' });
  },

  /**
   * Unlink a specific auth provider from the current account.
   * DELETE /api/me/identities/:provider
   */
  unlinkIdentity(provider: AuthProvider): Promise<UnlinkResponse> {
    return request<UnlinkResponse>(`/api/me/identities/${provider}`, { method: 'DELETE' });
  },

  /**
   * Soft-delete the current account.
   * DELETE /api/me  { confirm: 'DELETE' }
   */
  deleteAccount(confirm: 'DELETE'): Promise<DeleteAccountResponse> {
    return request<DeleteAccountResponse>('/api/me', {
      method: 'DELETE',
      body: { confirm },
    });
  },
};